// src/pages/Profile/Profile.jsx
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../components/axiosComp";

import FormButton from "../../components/form/FormButton";
import FormField from "../../components/form/FormField";
import FormInput from "../../components/form/FormInput";

import "./profile.css";

const Profile = () => {
  const adminCtx = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("va_ctx") || "{}");
    } catch {
      return {};
    }
  }, []);

  const name =
    adminCtx?.name ||
    adminCtx?.fullName ||
    (adminCtx?.email ? adminCtx.email.split("@")[0] : "Admin");

  const email = adminCtx?.email || "—";
  const role = adminCtx?.role || "—";
  const country = adminCtx?.country || null;

  const venues =
    Array.isArray(adminCtx?.venues) && adminCtx.venues.length
      ? adminCtx.venues.join(", ")
      : null;

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};

    if (!oldPassword) {
      nextErrors.oldPassword = "Current password is required";
    }

    if (!newPassword) {
      nextErrors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      nextErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!confirm) {
      nextErrors.confirm = "Please confirm your new password";
    } else if (newPassword !== confirm) {
      nextErrors.confirm = "Passwords do not match";
    }

    setErrors(nextErrors);

    return nextErrors;
  };

  const submit = async (event) => {
    event.preventDefault();

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix validation errors");
      return;
    }

    try {
      setSaving(true);

      await api.post("/api/auth/staff/change-password", {
        oldPassword,
        newPassword,
      });

      toast.success("Password updated successfully.");

      setOldPassword("");
      setNewPassword("");
      setConfirm("");
      setErrors({});
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Unable to update password. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Profile</h1>
        <div className="sa-note">Manage your account details & security</div>
      </div>

      <div className="profile-grid">
        <section className="sa-panel profile-card">
          <div className="sa-panel-hd profile-card-head">
            <h2>Admin Information</h2>
            <div className="sa-note">Account overview</div>
          </div>

          <div className="profile-info-list">
            <InfoRow label="Name" value={name} />
            <InfoRow label="Email" value={email} />
            <InfoRow label="Role" value={role} />

            {country && <InfoRow label="Country" value={country} />}
            {venues && <InfoRow label="Venues" value={venues} />}
          </div>
        </section>

        <section className="sa-panel profile-card">
          <div className="sa-panel-hd profile-card-head">
            <h2>Security</h2>
            <div className="sa-note">Change your password</div>
          </div>

          <form onSubmit={submit} noValidate>
            <div className="profile-form-grid">
              <FormField label="Current Password" error={errors.oldPassword}>
                <FormInput
                  type="password"
                  value={oldPassword}
                  onChange={(event) => {
                    setOldPassword(event.target.value);
                    clearError("oldPassword");
                  }}
                  placeholder="Enter current password"
                  disabled={saving}
                  autoComplete="current-password"
                />
              </FormField>

              <FormField label="New Password" error={errors.newPassword}>
                <FormInput
                  type="password"
                  value={newPassword}
                  onChange={(event) => {
                    setNewPassword(event.target.value);
                    clearError("newPassword");
                    clearError("confirm");
                  }}
                  placeholder="Enter new password"
                  disabled={saving}
                  autoComplete="new-password"
                />
              </FormField>

              <FormField label="Confirm Password" error={errors.confirm}>
                <FormInput
                  type="password"
                  value={confirm}
                  onChange={(event) => {
                    setConfirm(event.target.value);
                    clearError("confirm");
                  }}
                  placeholder="Re-enter new password"
                  disabled={saving}
                  autoComplete="new-password"
                />
              </FormField>
            </div>

            <div className="profile-actions">
              <FormButton type="submit" variant="primary" disabled={saving}>
                {saving ? "Updating..." : "Update Password"}
              </FormButton>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="profile-info-row">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

export default Profile;